/**
 * ImageItemWithQuery - TanStack Query版の画像アイテムコンポーネント
 */

import { useRef } from 'react'
import { useImage, usePrefetchAdjacentImages } from '../hooks/useImage'
import { usePinchZoom } from '../hooks/useSwipeNavigation'
import type { SetItem } from '../../../core/types'

interface ImageItemWithQueryProps {
  item: SetItem
  width: number
  height: number
  adjacentItems?: {
    prev?: SetItem
    next?: SetItem
  }
}

export function ImageItemWithQuery({ 
  item, 
  width, 
  height,
  adjacentItems
}: ImageItemWithQueryProps) {
  const imageRef = useRef<HTMLImageElement>(null)
  
  // TanStack Queryを使用して画像を取得
  const { url: imageUrl, isLoading, isError } = useImage(item.imageId)
  
  // 隣接画像のプリフェッチ
  usePrefetchAdjacentImages(
    item.imageId,
    {
      prev: adjacentItems?.prev?.imageId,
      next: adjacentItems?.next?.imageId
    }
  )
  
  // ピンチズーム機能
  usePinchZoom(imageRef, {
    enabled: true,
    minScale: 1,
    maxScale: 3
  })

  // ローディング状態
  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    )
  }

  // エラー状態
  if (isError || !imageUrl) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <div className="text-gray-500">
          <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="mt-2 text-sm">画像を読み込めません</p>
        </div>
      </div>
    )
  }

  // 画像表示
  return (
    <img
      ref={imageRef}
      src={imageUrl}
      alt={`Image ${item.order}`}
      className="max-w-full max-h-full object-contain touch-none"
      style={{
        width: 'auto',
        height: 'auto',
        maxWidth: `${width}px`,
        maxHeight: `${height}px`
      }}
      loading="eager" // 仮想スクロールなので遅延読み込みは不要
    />
  )
}